import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AdminData from "../../backend/dataservices/AdminData";

function AdminPermissionsDialog({ user, onClose, onSaved }) {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [tables, setTables] = React.useState([]);
    const [disciplines, setDisciplines] = React.useState([]);
    // { [tableName]: { r: bool, w: bool, d: bool } }
    const [perms, setPerms] = React.useState({});
    // Set of selected discipline codes for TAB_MITARBEITER
    const [selectedDiszis, setSelectedDiszis] = React.useState(new Set());
    const [disziExpanded, setDisziExpanded] = React.useState(false);

    React.useEffect(() => {
        if (!user) return;
        setLoading(true);
        setError(null);
        AdminData.getUserPermissions(user.U_ID, user.U_TenantID).then(data => {
            setTables(data.tables || []);
            setDisciplines(data.disciplines || []);

            const permMap = {};
            for (const t of (data.tables || [])) {
                permMap[t] = { r: false, w: false, d: false };
            }

            const disziSet = new Set();
            for (const p of (data.permissions || [])) {
                if (permMap[p.R_Tabelle]) {
                    permMap[p.R_Tabelle][p.R_Recht] = true;
                }
                if (p.R_Tabelle === 'TAB_MITARBEITER' && p.R_Diszi) {
                    disziSet.add(p.R_Diszi);
                }
            }

            setPerms(permMap);
            setSelectedDiszis(disziSet);
            if (disziSet.size > 0) setDisziExpanded(true);
            setLoading(false);
        }).catch(() => {
            setError('Fehler beim Laden der Berechtigungen.');
            setLoading(false);
        });
    }, [user]);

    function toggleRight(table, right) {
        setPerms(prev => ({
            ...prev,
            [table]: { ...prev[table], [right]: !prev[table][right] }
        }));
    }

    function toggleAllRights(table) {
        setPerms(prev => {
            const current = prev[table];
            const allChecked = current.r && current.w && current.d;
            return {
                ...prev,
                [table]: { r: !allChecked, w: !allChecked, d: !allChecked }
            };
        });
    }

    function toggleDiszi(diszi) {
        setSelectedDiszis(prev => {
            const next = new Set(prev);
            if (next.has(diszi)) next.delete(diszi);
            else next.add(diszi);
            return next;
        });
    }

    function toggleAllDiszis() {
        if (selectedDiszis.size === disciplines.length) {
            setSelectedDiszis(new Set());
        } else {
            setSelectedDiszis(new Set(disciplines));
        }
    }

    function handleSave() {
        setSaving(true);
        setError(null);

        const permissions = [];
        for (const table of tables) {
            const p = perms[table];
            const rights = ['r', 'w', 'd'].filter(r => p[r]);
            if (rights.length === 0) continue;

            const entry = { table, rights };
            if (table === 'TAB_MITARBEITER') {
                entry.disciplines = [...selectedDiszis];
            }
            permissions.push(entry);
        }

        AdminData.updateUserPermissions(user.U_ID, user.U_TenantID, permissions).then(() => {
            setSaving(false);
            if (onSaved) onSaved();
            onClose();
        }).catch(() => {
            setError('Fehler beim Speichern der Berechtigungen.');
            setSaving(false);
        });
    }

    const hasMitarbeiterRights = perms['TAB_MITARBEITER'] &&
        (perms['TAB_MITARBEITER'].r || perms['TAB_MITARBEITER'].w || perms['TAB_MITARBEITER'].d);

    return (
        <Dialog open={user !== null} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Berechtigungen: {user?.U_FullName || user?.U_Benutzername}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}
                <Alert severity="info" sx={{ mb: 2 }}>
                    Beim Zuweisen einer neuen Rolle werden diese Berechtigungen auf die Standard-Vorlage zurückgesetzt.
                </Alert>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Tabelle</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Lesen</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Schreiben</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Löschen</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Alle</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tables.map(table => (
                                        <React.Fragment key={table}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {table}
                                                        {table === 'TAB_MITARBEITER' && hasMitarbeiterRights && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setDisziExpanded(!disziExpanded)}
                                                                sx={{ ml: 1 }}
                                                            >
                                                                {disziExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={perms[table]?.r || false}
                                                        onChange={() => toggleRight(table, 'r')}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={perms[table]?.w || false}
                                                        onChange={() => toggleRight(table, 'w')}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={perms[table]?.d || false}
                                                        onChange={() => toggleRight(table, 'd')}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={perms[table]?.r && perms[table]?.w && perms[table]?.d}
                                                        indeterminate={
                                                            (perms[table]?.r || perms[table]?.w || perms[table]?.d) &&
                                                            !(perms[table]?.r && perms[table]?.w && perms[table]?.d)
                                                        }
                                                        onChange={() => toggleAllRights(table)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            {table === 'TAB_MITARBEITER' && hasMitarbeiterRights && (
                                                <TableRow>
                                                    <TableCell colSpan={5} sx={{ py: 0, borderBottom: disziExpanded ? undefined : 'none' }}>
                                                        <Collapse in={disziExpanded}>
                                                            <Box sx={{ py: 1, pl: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                                                                        Führungskräfte ({selectedDiszis.size}/{disciplines.length})
                                                                    </Typography>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                size="small"
                                                                                checked={selectedDiszis.size === disciplines.length}
                                                                                indeterminate={selectedDiszis.size > 0 && selectedDiszis.size < disciplines.length}
                                                                                onChange={toggleAllDiszis}
                                                                            />
                                                                        }
                                                                        label={<Typography variant="body2">Alle</Typography>}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {disciplines.map(d => (
                                                                        <Chip
                                                                            key={d}
                                                                            label={d}
                                                                            size="small"
                                                                            variant={selectedDiszis.has(d) ? "filled" : "outlined"}
                                                                            color={selectedDiszis.has(d) ? "primary" : "default"}
                                                                            onClick={() => toggleDiszi(d)}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} style={{ color: "gray" }}>Abbrechen</Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    disabled={saving || loading}
                >
                    {saving ? <CircularProgress size={20} /> : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AdminPermissionsDialog;
